import { useContext, useEffect, useState } from 'react';
import {
  bundleIcon,
  ClipboardTaskListLtr20Filled,
  ClipboardTaskListLtr20Regular,
  BrainSparkle20Filled,
  BrainSparkle20Regular,
  BookDatabase20Filled,
  BookDatabase20Regular,
  DocumentBulletList20Filled,
  DocumentBulletList20Regular,
  BranchRequest20Filled,
  BranchRequest20Regular,
  BuildingFactory20Filled,
  BuildingFactory20Regular,
  ClipboardTask20Filled,
  ClipboardTask20Regular,
  Folder20Filled,
  Folder20Regular,
  Library20Filled,
  Library20Regular,
  Open16Regular,
  Flowchart20Filled,
  Flowchart20Regular,
  HatGraduation20Filled,
  HatGraduation20Regular,
  Screenshot20Filled,
  Screenshot20Regular,
  Send20Filled,
  Send20Regular,
  Settings20Filled,
  Settings20Regular,
} from '@fluentui/react-icons';
import { NavCategory, NavSectionHeader, NavSubItemGroup } from '@fluentui/react-nav-preview';
import { Subtitle2, makeStyles, tokens } from '@fluentui/react-components';

import { Link } from 'react-router-dom';

import { GatedNavLink, NavCategoryItem, NavDrawer, NavItem, NavSubItem } from '@shared/components';
import { EFolderType, TFolder, TPath } from '@modules/items';
import { AccessType } from '@modules/access';
import { SignInContext, USER_GROUPS } from '@modules/users';
import { GlobalLibraryFilled, GlobalLibraryRegular } from '@shared/icons';
import { TERMINOLOGY } from '@shared/i18n';
import { useSearchParams } from '@shared/hooks';
import { ToastContext } from '@shared/contexts';
import { getChangeDashboardPath, isChangeMockVisible } from '@modules/mocks/change/helpers';
import { getChangeV2DashboardPath, isChangeV2MockVisible } from '@modules/mocks/changeV2/helpers';
import { getReviewsPath, isReviewMockVisible } from '@modules/mocks/review/helpers';
import { isTrainingMockVisible } from '@modules/mocks/training/helpers';
import { getProjectsPath } from '@modules/projects/paths';
import { CHANGE_TEMPLATES_PATH } from '@modules/changeTemplates/paths';
import { isChangeTemplatesEnabled } from '@modules/changeTemplates/helpers';
import { TRAINING_RECORDS_PATH } from '@modules/mocks/training/constants';
import RegistrationsNavLink from '@modules/registrations/components/RegistrationsNavLink';

type TProps = {
  folder: TFolder;
  fullPath: TPath;
  selectedValue: string;
  /** Whether the drawer starts expanded. Defaults to open; the changes module starts it collapsed. */
  initialOpen?: boolean;
};

const ProjectIcon = bundleIcon(ClipboardTaskListLtr20Filled, ClipboardTaskListLtr20Regular);
const FolderIcon = bundleIcon(Folder20Filled, Folder20Regular);
const SendIcon = bundleIcon(Send20Filled, Send20Regular);
const SettingsIcon = bundleIcon(Settings20Filled, Settings20Regular);
const ProductLibraryIcon = bundleIcon(Library20Filled, Library20Regular);
const ScreenshotIcon = bundleIcon(Screenshot20Filled, Screenshot20Regular);
const LiteratureIcon = bundleIcon(BookDatabase20Filled, BookDatabase20Regular);
const ChangeTemplateIcon = bundleIcon(DocumentBulletList20Filled, DocumentBulletList20Regular);
const ChangeIcon = bundleIcon(BranchRequest20Filled, BranchRequest20Regular);
const ChangeV2Icon = bundleIcon(Flowchart20Filled, Flowchart20Regular);
const ReviewIcon = bundleIcon(BrainSparkle20Filled, BrainSparkle20Regular);
const TrainingIcon = bundleIcon(HatGraduation20Filled, HatGraduation20Regular);
const SupplierIcon = bundleIcon(BuildingFactory20Filled, BuildingFactory20Regular);
const CapaIcon = bundleIcon(ClipboardTask20Filled, ClipboardTask20Regular);

const useStyles = makeStyles({
  navItemLink: {
    '& button': {
      // add className to NavItem is not possible
      textAlign: 'left', // long text auto aligns to center
    },
  },
  openIcon: {
    color: tokens.colorNeutralForeground4,
  },
});

const FoldersNavDrawer = ({ folder, fullPath, selectedValue, initialOpen = true }: TProps) => {
  const { user, organization, folderAccess } = useContext(SignInContext);
  const { show } = useContext(ToastContext);
  const isImpactAnalysisEnabled = organization.featureSettings.impactAnalysis?.enabled;
  const isSnapshotsEnabled = organization.featureSettings.snapshots?.enabled;
  const isChangeMockEnabled = isChangeMockVisible(organization.mock?.change);
  const isChangeV2MockEnabled = isChangeV2MockVisible(organization.mock?.changeV2);
  const isReviewMockEnabled = isReviewMockVisible(organization.mock?.review);
  const isTrainingMockEnabled = isTrainingMockVisible(organization.mock?.training);
  const rootItemId = fullPath[0]._id;
  // Optional-chained: a session signed in before the backend restart has no map yet.
  const projectsFolderAccess = folderAccess.projectsFolders?.[rootItemId];
  const isQm = organization.folders.qm === rootItemId;
  const isCountriesRoot = organization.folders.countries === rootItemId;
  const isCountriesSpace = isCountriesRoot || folder.folderType === EFolderType.COUNTRY;
  const [isOpen, setIsOpen] = useState(initialOpen);

  const { searchParams } = useSearchParams();
  // Close drawer whenever “wide=true” is in the URL
  const shouldClose = searchParams.get('wide') === 'true';
  useEffect(() => {
    if (shouldClose) setIsOpen(false);
  }, [shouldClose]);

  const projectRootItemType = isQm ? EFolderType.QM : isCountriesRoot ? EFolderType.COUNTRY : EFolderType.PRODUCT;
  const rootItemHref = `/${isQm || isCountriesRoot ? 'folder' : 'product'}/${rootItemId}`;
  // Both come from the access-filtered rootChildren: absent means no access, so the link is disabled and the href unused.
  const productLibraryHref = folder.blocksFolder ? `/folder/${folder.blocksFolder._id}` : '';
  const snapshotsHref = folder.snapshotsFolder ? `/folder/${folder.snapshotsFolder._id}` : '';

  const classes = useStyles();
  const showModuleWarning = () => show('warning', 'Please ask your admin to enable this module.');
  return (
    <NavDrawer
      type="inline"
      title={fullPath[0].name}
      titleHref={rootItemHref}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      selectedValue={selectedValue}
    >
      <Link to={rootItemHref} className={classes.navItemLink}>
        <NavItem icon={<FolderIcon />} value={isCountriesSpace ? 'registrations' : 'overview'}>
          {isCountriesSpace ? 'Countries' : isQm ? 'Files' : 'Product'}
        </NavItem>
      </Link>
      {isCountriesSpace && <RegistrationsNavLink className={classes.navItemLink} />}
      {!isQm && !isCountriesSpace && (
        <GatedNavLink
          hasAccess={!!folder.blocksFolder}
          disabledMessage={`You don't have access rights for ${TERMINOLOGY.productDatabase.lower}.`}
          to={productLibraryHref}
          value="product-library"
          icon={<ProductLibraryIcon />}
          className={classes.navItemLink}
        >
          {TERMINOLOGY.productDatabase.upper}
        </GatedNavLink>
      )}

      {/* qmBlocks is only ever written together with qm, so it implies both are configured */}
      {organization.folders.qmBlocks && (
        <GatedNavLink
          hasAccess={folderAccess.qmBlocks >= AccessType.PARTIAL}
          disabledMessage={`You don't have access rights for ${TERMINOLOGY.qmDatabase.lower}.`}
          to={`/folder/${organization.folders.qmBlocks}`}
          target={isQm ? '_self' : '_blank'}
          value="global-library"
          icon={
            selectedValue === 'global-library' ? (
              <GlobalLibraryFilled color={tokens.colorBrandBackground} />
            ) : (
              <GlobalLibraryRegular />
            )
          }
          className={classes.navItemLink}
        >
          <div className="w-full flex items-center justify-between">
            <span>{TERMINOLOGY.qmDatabase.upper}</span>
            {!isQm && <Open16Regular className={classes.openIcon} />}
          </div>
        </GatedNavLink>
      )}

      {/* Literature lives under QM (future "global workspace"), so the link
          always targets the QM folder context regardless of where it is opened from.
          When opened from a product, productId is passed as a query param so the list
          pre-filters to that product's assessments. Same tab when already in QM,
          new tab from product pages. Hidden on countries and only shown when the
          feature is enabled for the org (a per-user access gate, not feature presence,
          is handled by GatedNavLink below). */}
      {!isCountriesSpace &&
        organization.folders.qm &&
        organization.featureSettings?.literatures?.enabled &&
        organization.folders.literature && (
          <GatedNavLink
            hasAccess={folderAccess.literature >= AccessType.READ}
            disabledMessage={`You don't have access rights for ${TERMINOLOGY.literature.lower}.`}
            to={`/literatures${isQm ? '' : `?productIds=${rootItemId}`}`}
            target={isQm ? '_self' : '_blank'}
            value="literatures"
            icon={<LiteratureIcon />}
            className={classes.navItemLink}
          >
            <div className="w-full flex items-center justify-between">
              <span>{TERMINOLOGY.literature.upper}</span>
              {!isQm && <Open16Regular className={classes.openIcon} />}
            </div>
          </GatedNavLink>
        )}

      {isQm && isChangeTemplatesEnabled(organization) && (
        <GatedNavLink
          hasAccess={folderAccess.changeTemplates >= AccessType.PARTIAL}
          disabledMessage={`You don't have access rights for ${TERMINOLOGY.changeTemplate.lowerPlural}.`}
          to={CHANGE_TEMPLATES_PATH}
          value="change-templates"
          icon={<ChangeTemplateIcon />}
          className={classes.navItemLink}
        >
          {TERMINOLOGY.changeTemplate.upperPlural}
        </GatedNavLink>
      )}

      {/* Change management V1 lives under product spaces. */}
      {!isCountriesSpace && !isQm && isChangeMockEnabled && (
        <Link to={getChangeDashboardPath(rootItemId)} className={classes.navItemLink}>
          <NavItem icon={<ChangeIcon />} value="changes">
            Change
          </NavItem>
        </Link>
      )}

      {/* Change management V2 is the company-wide register and lives under QM. */}
      {!isCountriesSpace && isQm && isChangeV2MockEnabled && (
        <Link to={getChangeV2DashboardPath()} className={classes.navItemLink}>
          <NavItem icon={<ChangeV2Icon />} value="change-v2">
            Changes
          </NavItem>
        </Link>
      )}

      {/* Training records prototype: company-wide QM training matrix. */}
      {!isCountriesSpace && isQm && isTrainingMockEnabled && (
        <Link to={TRAINING_RECORDS_PATH} className={classes.navItemLink}>
          <NavItem icon={<TrainingIcon />} value="training">
            Training
          </NavItem>
        </Link>
      )}

      {/* Product spaces open TD reviews for that product; QM opens only QM reviews. */}
      {!isCountriesSpace && isReviewMockEnabled && (
        <Link to={getReviewsPath(isQm ? undefined : rootItemId)} className={classes.navItemLink}>
          <NavItem icon={<ReviewIcon />} value="review">
            AI Review
          </NavItem>
        </Link>
      )}

      {!isQm && !isCountriesSpace && (
        <>
          {/*['jan.schweiger@confideck.com', 'lukas.poterpin@confideck.com', 'rulsan.kotliarenko@confideck.com'].includes(
            user.mail
          ) && (
            <Link to={`/compliance`} className={classes.navItemLink}>
              <NavItem icon={<DataBarVerticalStar20Regular />} value="compliance">
                Compliance dashboard
              </NavItem>
            </Link>
          )*/}
          <Link to={`/product/${rootItemId}/submissions`} className={classes.navItemLink}>
            <NavItem icon={<SendIcon />} value="submissions">
              Submissions
            </NavItem>
          </Link>
        </>
      )}

      {organization.featureSettings.projects?.enabled &&
        organization.featureSettings.projects.enabledRootItemTypes?.includes(projectRootItemType) &&
        projectsFolderAccess && (
          <GatedNavLink
            hasAccess={projectsFolderAccess.accessRight >= AccessType.PARTIAL}
            disabledMessage={`You don't have access rights for ${TERMINOLOGY.project.lowerPlural}.`}
            to={getProjectsPath(projectsFolderAccess.folder)}
            value="projects"
            icon={<ProjectIcon />}
            className={classes.navItemLink}
          >
            {TERMINOLOGY.project.upperPlural}
          </GatedNavLink>
        )}

      {!isCountriesSpace && isSnapshotsEnabled && (
        <GatedNavLink
          hasAccess={!!folder.snapshotsFolder}
          disabledMessage={`You don't have access rights for ${TERMINOLOGY.snapshots.lower}.`}
          to={snapshotsHref}
          value="snapshots"
          icon={<ScreenshotIcon />}
          className={classes.navItemLink}
        >
          {TERMINOLOGY.snapshots.upper}
        </GatedNavLink>
      )}

      {user.groups.includes(USER_GROUPS.ADMIN) && !isCountriesSpace && (
        <NavCategory value="setup">
          <NavCategoryItem icon={<SettingsIcon />} value="setup">
            Setup
          </NavCategoryItem>
          <NavSubItemGroup>
            <Link to={`/product/${rootItemId}/reference-detection`} className={classes.navItemLink}>
              <NavSubItem value="reference-detection">{TERMINOLOGY.reference.upper} detection</NavSubItem>
            </Link>
            <Link to={`/product/${rootItemId}/library-detection`} className={classes.navItemLink}>
              <NavSubItem value="library-detection">Library detection</NavSubItem>
            </Link>
            {!isQm && isImpactAnalysisEnabled && (
              <Link to={`/product/${rootItemId}/tags`} className={classes.navItemLink}>
                <NavSubItem value="tags">Regulatory tags</NavSubItem>
              </Link>
            )}
          </NavSubItemGroup>
        </NavCategory>
      )}

      {!isCountriesSpace && isQm && isTrainingMockEnabled && (
        <>
          <NavItem icon={<SupplierIcon />} value="supplier-management" onClick={showModuleWarning}>
            Supplier Management
          </NavItem>
          <NavItem icon={<CapaIcon />} value="capa" onClick={showModuleWarning}>
            CAPA
          </NavItem>
        </>
      )}

      {!isQm && !isCountriesSpace && (
        <>
          <div className="mt-3">
            {/*<Divider />*/}
            <NavSectionHeader>
              <Subtitle2>Jurisdictions</Subtitle2>
            </NavSectionHeader>
            {folder.jurisdictions.map((j) => (
              <Link key={j._id} to={`/folder/${j._id}`} className={classes.navItemLink}>
                <NavItem value={j._id} size="small">
                  {j.name}
                </NavItem>
              </Link>
            ))}
          </div>
        </>
      )}
    </NavDrawer>
  );
};

export default FoldersNavDrawer;
